import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FormApi } from 'final-form';
import { Box } from '@mui/material';

import { LoadingOverlay } from '@occc/ui-design-system';
import { OfferFilter, OfferFragment } from '@occc/graphql';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';

import { useCallback } from 'react';
import { ConfigureFormContent } from './ConfigureFormContent';

import { validateConfiguration } from '../../validation';
import { ConfigureFormValues } from '../../types';
import { useOfferReturnStrategy } from '../../../../Chain/hooks/useOfferReturnStrategy';

type ConfigureFormProps = {
  onSubmit: (values: ConfigureFormValues, form: FormApi<ConfigureFormValues>) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: Partial<ConfigureFormValues>;
  makeOffersFilter: (search: string) => OfferFilter;
  loading: boolean;
  currentFlowType: Flow_Types;
  isFormEditable: boolean;
  onNextStepButtonClick?: () => void;
};

export type Offer = Pick<OfferFragment, 'id' | 'name' | 'engagementType'>;

export const ConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  makeOffersFilter,
  loading,
  currentFlowType,
  isFormEditable,
  onNextStepButtonClick,
}: ConfigureFormProps) => {
  const offerReturnStrategy = useOfferReturnStrategy();

  const renderForm: FormRenderProps<ConfigureFormValues>['render'] = useCallback(
    ({ handleSubmit, submitting, values, dirty }: FormRenderProps<ConfigureFormValues>) => {
      return (
        <ConfigureFormContent
          handleSubmit={handleSubmit}
          submitting={submitting}
          values={values}
          dirty={dirty}
          currentFlowType={currentFlowType}
          isFormEditable={isFormEditable}
          makeOffersFilter={makeOffersFilter}
          offerReturnStrategy={offerReturnStrategy}
          onCancelClick={onCancelClick}
          onNextStepButtonClick={onNextStepButtonClick}
        />
      );
    },
    [
      currentFlowType,
      isFormEditable,
      makeOffersFilter,
      offerReturnStrategy,
      onCancelClick,
      onNextStepButtonClick,
    ],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<ConfigureFormValues>
          onSubmit={onSubmit}
          render={renderForm}
          mutators={{ ...arrayMutators }}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
